/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  alpha,
  Box,
  CircularProgress,
  Collapse,
  Grid,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { Editor as TinyMCEEditor } from 'tinymce';
import { Theme, useTheme } from '@mui/material/styles';
import AssistantIcon from '@mui/icons-material/Assistant';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import makeStyles from '@mui/styles/makeStyles';
import { ConversableType } from '../../enums/Conversations';
import { ConversationSubmitInput } from './Input';
import { ConversationThreadAttachmentInput } from '../../graphql/ConversationThreads/ConversationThreadsInterface';
import {
  EXECUTABLE_EXTENSIONS,
  EXECUTABLE_MIMES,
} from '../../config/variables';
import { getExtension } from '../../lib/File';
import { getNumericSpacing } from '../../lib/Styles';
import { MentionProps } from '../Input/RichTextEditor';
import { OrganizationFlagEnum } from '../../lib/features/FeatureFlag';
import { removeElement, updateElement } from '../../lib/Array';
import { ROOM_CONVERSATION_TOOLBAR } from '../Input/helper';
import { useI18n } from '../../lib/hook/useI18n';
import Attachment from './Attachment';
import AttachmentEntry, { AttachmentModel } from './AttachmentEntry';
import CloudStorage from '../../Services/CloudStorage';
import Dropzone, { RenderInputField } from '../FileSelector/Dropzone';
import OrganizationsStore from '../../stores/observables/OrganizationsStore';
import SnackbarStore from '../../stores/observables/SnackbarStore';
import TextEditors from '../Input/TextEditors';
import ThemableButton from '../../theme/components/Button/ThemableButton';
import ThemableIconButton from '../../theme/components/Button/ThemableIconButton';
import useFeatureFlag from '../../lib/hook/useFeatureFlag';

const MAX_FILESIZE_MB = 50;
// We use 1000 to match https://www.npmjs.com/package/filesize
const MAX_FILESIZE_B = MAX_FILESIZE_MB * 1000 * 1000;

const getAttachmentPath = (
  file: File,
  conversableType?: ConversableType,
  conversableId?: string
) =>
  [
    `conversation-${conversableType?.toLowerCase()}-${conversableId}`,
    `attachment-${Date.now()}-${uuidv4()}.${getExtension(file)}`,
  ].join('/');

const useStyles = makeStyles(() => ({
  button: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const useDropzoneStyles = makeStyles((theme: Theme) => ({
  dragActive: {},
  root: {
    borderRadius: theme.spacing(1),
    display: 'block',
    marginTop: theme.spacing(1),
    outline: 'none',
    width: '100%',
    '&$dragActive': {
      background: alpha(theme.palette.secondary.light, 0.15),
      border: `${theme.spacing(0.25)} dashed ${theme.palette.secondary.main}`,
    },
  },
  withFiles: {},
  withoutFiles: {},
}));

interface SetEntryStatusInput {
  completed: boolean;
  error: string | null;
  uploading: boolean;
}

interface AttachmentsControllerApi {
  renderAttachmentButton: () => React.ReactNode;
  renderAssistantButton: (onAssist: () => Promise<any>) => React.ReactNode;
  renderAttachments: () => React.ReactNode;
  renderInput: (inputRef?: TextFieldProps['inputRef']) => React.ReactNode;
  renderSubmitButton: () => React.ReactNode;
}

export interface ConversationFormProps {
  attachments: AttachmentEntry[];
  authorId?: string;
  buttonLabel?: React.ReactNode;
  children: (controller: AttachmentsControllerApi) => React.ReactNode;
  conversableId?: string;
  conversableType?: ConversableType;
  disabled?: boolean;
  isReplying?: boolean;
  mentions?: MentionProps;
  message: string;
  multiline?: boolean;
  onSubmit: (input: ConversationSubmitInput) => Promise<void>;
  onTextInputClick: () => void;
  placeholder?: string;
  saving?: boolean;
  setAttachments: (attachments: AttachmentEntry[]) => void;
  setMessage: (value: string) => void;
  setSaving: (saving: boolean) => void;
  dataTestId?: string;
}

const ConversationForm: React.FC<ConversationFormProps> = (
  props: ConversationFormProps
) => {
  const { t } = useI18n();
  const classes = useStyles();
  const dropzoneClasses = useDropzoneStyles();
  const theme = useTheme();

  const canAssist = useFeatureFlag(OrganizationFlagEnum.OPEN_AI);

  const {
    attachments,
    authorId,
    buttonLabel = 'post',
    children,
    conversableId,
    conversableType,
    disabled = false,
    isReplying,
    mentions,
    message,
    multiline = false,
    onSubmit,
    onTextInputClick,
    placeholder,
    saving = false,
    setAttachments,
    setMessage,
    setSaving,
    dataTestId,
  } = props;

  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [rteEnabled, setRTEEnabled] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const toolbarType = ROOM_CONVERSATION_TOOLBAR;

  const addFiles = (filesToAdd: File[]) => {
    const newAttachments = filesToAdd.map(
      (file) =>
        new AttachmentEntry(
          AttachmentModel({
            filename: file.name,
            filesize: file.size || 0,
            mime: file.type,
          }),
          file
        )
    );

    setAttachments(attachments.concat(newAttachments));
  };

  useEffect(() => {
    let isRteEnable = isReplying || false;
    // special scenario when mentions are enabled and we need put the focus on the RTE box
    if (mentions?.enabled && mentions?.rteEnabled && !isReplying) {
      isRteEnable = true;
    }
    if (message.trim().length > 0) {
      isRteEnable = true;
    }
    setRTEEnabled(isRteEnable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReplying, message]);

  const checkFile = useCallback(
    (fileToAdd: File) =>
      !attachments.find(
        // Compare files by name
        // @todo figure out a better way to compare File objects
        (attachment) => attachment.model.filename === fileToAdd.name
      ),
    [attachments]
  );

  /**
   * Prefer triggering the file selector programatically due
   * to accessibility reasons (better than <label />).
   */
  const onAttachmentButtonClick = () => {
    fileInputRef.current?.click();
  };
  const editorRef = useRef<TinyMCEEditor>();

  const getRenderAttachmentButton =
    (renderInputField: RenderInputField) => () =>
      (
        <Tooltip
          arrow
          placement="top"
          PopperProps={{ disablePortal: true }}
          title={t('conversation.attach_file')}
          enterTouchDelay={0}
          leaveTouchDelay={5000}
        >
          <div>
            <ThemableIconButton
              aria-label="Attach file"
              classes={{ root: classes.button }}
              disabled={saving}
              onClick={onAttachmentButtonClick}
              size="small"
            >
              <AttachFileIcon
                sx={(theme) => ({ fontSize: theme.spacing(2.4) })}
              />
            </ThemableIconButton>
            {renderInputField()}
          </div>
        </Tooltip>
      );

  const setEntryStatus = (
    index: number,
    { completed, error, uploading }: SetEntryStatusInput
  ) => {
    setAttachments(
      updateElement(attachments, index, (entry) => {
        entry.completed = completed;
        entry.uploading = uploading;
        entry.error = error;
        return entry;
      })
    );
  };

  const setEntryModel = (
    index: number,
    input: ConversationThreadAttachmentInput
  ) => {
    setAttachments(
      updateElement(attachments, index, (entry) => {
        entry.model.uri = input.uri;
        entry.completed = true;
        entry.uploading = false;
        entry.error = null;
        return entry;
      })
    );
  };

  const uploadAttachment = async (
    entry: AttachmentEntry
  ): Promise<ConversationThreadAttachmentInput> => {
    if (!conversableType || !conversableId) {
      throw new Error('Missing conversable props');
    }
    const { file } = entry;
    const organizationId = OrganizationsStore.getCurrent();
    const result = await CloudStorage.uploadAttachment(
      { contactId: authorId, organizationId },
      file,
      getAttachmentPath(file, conversableType, conversableId)
    );
    return {
      ...result,
      filesize: file.size || 0,
    };
  };

  const uploadAttachments = async () =>
    new Promise<ConversationThreadAttachmentInput[]>((resolve, reject) => {
      const uploadPromises: Promise<ConversationThreadAttachmentInput | void>[] =
        [];
      attachments.forEach((entry, index) => {
        // If the file was already uploaded, then avoid re-processing it
        if (entry.completed) {
          return;
        }
        // Errors must be cleaned before uploading
        setEntryStatus(index, {
          completed: false,
          error: null,
          uploading: true,
        });
        // Define callback functions
        const onSuccess = (input: ConversationThreadAttachmentInput) => {
          setEntryModel(index, input);
          return input;
        };
        const onError = (error: string) => {
          setEntryStatus(index, {
            completed: false,
            error,
            uploading: false,
          });
        };
        // Create the upload promises
        uploadPromises.push(
          uploadAttachment(entry).then(onSuccess).catch(onError)
        );
      });
      // Notify the uploads were completed
      (Promise as any).allSettled(uploadPromises).then(
        (results: { value: ConversationThreadAttachmentInput }[]) =>
          resolve(results.map((r) => r.value)),
        (e: any) => reject(e)
      );
    });

  const onFormSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const msg = message.trim();

    // Validations
    if (attachments.length === 0 && msg === '') {
      return;
    }

    try {
      // Delegate submit flow
      setSaving(true);
      const conversationThreadAttachments = await uploadAttachments();
      await onSubmit({
        attachments:
          conversationThreadAttachments.length > 0
            ? conversationThreadAttachments
            : undefined,
        message: msg !== '' ? msg : undefined,
      });
      // On success, reset fields
      setAttachments([]);
      setMessage('');
    } catch (e) {
      SnackbarStore.show(`${e}`);
    } finally {
      setSaving(false);
    }
  };

  const onMessageChange = (text: string) => {
    setMessage(text);
  };

  const removeFile = (ix: number) => {
    setAttachments(removeElement(attachments, ix));
  };

  const onAssistantButtonClick = async (onAssist: () => Promise<any>) => {
    try {
      setLoadingSuggestion(true);
      await onAssist();
    } catch (e) {
      // do nothing
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const renderAssistantButton = (onAssist: () => Promise<any>) => {
    if (!canAssist) {
      return;
    }

    if (loadingSuggestion) {
      return (
        <Box py={0.25} pl={1.25}>
          <CircularProgress color="secondary" size={16} />
        </Box>
      );
    }

    return (
      <Tooltip
        arrow
        placement="top"
        PopperProps={{ disablePortal: true }}
        title="Magic ✨"
        enterTouchDelay={0}
        leaveTouchDelay={5000}
      >
        <div>
          <ThemableIconButton
            aria-label="Assistant"
            classes={{ root: classes.button }}
            disabled={loadingSuggestion || saving}
            onClick={() => onAssistantButtonClick(onAssist)}
            size="small"
          >
            <AssistantIcon sx={(theme) => ({ fontSize: theme.spacing(2.4) })} />
          </ThemableIconButton>
        </div>
      </Tooltip>
    );
  };

  const renderAttachments = () => (
    <Collapse appear in={attachments.length > 0} mountOnEnter unmountOnExit>
      <Box mt={1}>
        <Typography variant="overline">
          <strong>{t('conversation.drawer.attachments')}</strong>
        </Typography>
        <Grid container spacing={2}>
          {attachments.map((attachment: AttachmentEntry, ix) => (
            <Grid key={attachment.listId} item xs={12} sm={6}>
              <Attachment
                completed={attachment.completed}
                deletable
                disabled={disabled}
                filename={attachment.model.filename}
                mime={attachment.model.mime}
                onDelete={() => removeFile(ix)}
                uploading={attachment.uploading}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Collapse>
  );

  const renderInput = () => {
    if (!rteEnabled) {
      return (
        <TextField
          color="secondary"
          fullWidth
          id="outlined-read-only-input"
          InputProps={{
            readOnly: true,
            onClick: () => {
              setRTEEnabled(true);
              onTextInputClick();
            },
          }}
          placeholder={placeholder}
          variant="outlined"
        />
      );
    }

    return (
      <TextEditors
        additionalPlugins={['autoresize']}
        disabled={disabled}
        editorRef={editorRef}
        editorSize={getNumericSpacing(theme, 16)}
        html
        initialTextValue={message}
        maxSize={getNumericSpacing(theme, 54.375)}
        mentions={mentions}
        multiline={multiline}
        onChangeHandler={onMessageChange}
        placeholder={placeholder}
        resizeMargin={0}
        text={message}
        toolbarType={toolbarType}
      />
    );
  };

  const renderSubmitButton = () => (
    <ThemableButton
      disabled={
        disabled || (message.trim().length === 0 && attachments.length === 0)
      }
      onClick={onFormSubmit}
      size="small"
      variant="contained"
      dataTestId="button-post-message"
    >
      {buttonLabel}
    </ThemableButton>
  );

  return (
    <Dropzone
      ref={fileInputRef}
      checkFile={checkFile}
      classes={dropzoneClasses}
      maxFilesize={MAX_FILESIZE_B}
      render={(renderInputField) => (
        <form onSubmit={onFormSubmit} data-testid={dataTestId}>
          {children({
            renderAttachmentButton: getRenderAttachmentButton(renderInputField),
            renderAssistantButton,
            renderAttachments,
            renderInput,
            renderSubmitButton,
          })}
        </form>
      )}
      setFiles={addFiles}
      unsupportedFormatExtensions={EXECUTABLE_EXTENSIONS}
      unsupportedFormatMimes={EXECUTABLE_MIMES}
    />
  );
};

export default ConversationForm;
